import { cn, UzConfirmDialog } from '@utazzo/ui';
import { Check, Pencil, Search, UserRound } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useAgentOptionsForLeads } from '../use-lead-options';
import { useUpdateLead } from '../use-leads';

import type { LeadDetail } from '@utazzo/shared';

import { useCurrentUser } from '@/features/auth/use-auth';

/**
 * The lead's owner, in the detail header under the Call / WhatsApp buttons -
 * who holds the lead is as much a headline fact as its stage, and it used to
 * sit at the bottom of the Enquiry context card.
 *
 * It reads as plain text. The picker only appears after the pencil is clicked,
 * so the header carries no dropdown that a stray click could open, and the pick
 * is confirmed before anything moves.
 *
 * It is the SAME reassignment the Edit form's "Owner (assigned agent)" picker
 * performs - `PATCH /leads/:id` with `assignedAgentId` - so every rule lives
 * where it already did, server-side (`validateLeadOwnerReassignment` plus the
 * ADR-0057 sales-coverage gate). This control only mirrors those rules so the
 * operator is not offered a change that would 403:
 *
 *   - ADMIN: any assignable agent, OPEN leads only.
 *   - SALES_LEAD: self + direct reports, OPEN leads only. The option list is
 *     hierarchy-scoped by `/users/assignable-agents` itself.
 *   - Everyone else, and any closed (WON / LOST) lead: the name, no pencil.
 *   - Pencil locked while an approval request is pending, like Edit.
 *
 * Unassigning is left to the Edit form: clearing an owner from the header is
 * too easy to do by accident, and a sales lead may not do it at all.
 */
export const LeadOwnerChanger = ({
  lead,
  approvalLocked,
}: {
  lead: LeadDetail;
  /** An approval request is pending on the lead - same lock as Edit. */
  approvalLocked: boolean;
}): React.JSX.Element => {
  const me = useCurrentUser();
  const canReassign = me?.role === 'ADMIN' || me?.role === 'SALES_LEAD';
  const isOpenLead = lead.currentBucket === 'OPEN';
  const editable = canReassign && isOpenLead;

  const agents = useAgentOptionsForLeads();
  const update = useUpdateLead();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // The pick waits here for a confirm: reassigning moves the lead onto another
  // agent's desk and notifies them of it.
  const [pendingAgentId, setPendingAgentId] = useState<string | null>(null);

  const currentId = lead.assignedAgent?.id ?? null;
  const ownerName = lead.assignedAgent?.fullName ?? 'Unassigned';

  /** Case-insensitive substring match on the name; '' matches everyone. */
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents.options;
    return agents.options.filter((option) => option.label.toLowerCase().includes(q));
  }, [agents.options, query]);

  const pick = (agentId: string): void => {
    setOpen(false);
    if (agentId !== currentId) setPendingAgentId(agentId);
  };
  // A stale query would silently hide agents the next time it opens.
  const toggle = (): void => {
    setQuery('');
    setOpen((value) => !value);
  };

  const pendingAgentName =
    agents.options.find((option) => option.value === pendingAgentId)?.label ?? 'this agent';

  const confirmReassign = async (): Promise<void> => {
    if (!pendingAgentId) return;
    // Branch follows the agent, exactly as the Edit form's picker does it.
    const branchId = agents.branchByAgentId.get(pendingAgentId);
    try {
      await update.mutateAsync({
        id: lead.id,
        payload: {
          assignedAgentId: pendingAgentId,
          ...(branchId !== undefined ? { branchId } : {}),
        },
      });
    } catch {
      // The mutation already toasts the server's reason (not their report,
      // agent does not cover this destination, ...). Nothing more to do.
    } finally {
      setPendingAgentId(null);
    }
  };

  const pencilTitle = approvalLocked
    ? 'Actions are locked while an approval request is pending.'
    : me?.role === 'SALES_LEAD'
      ? 'Assign this lead to yourself or one of your direct reports.'
      : 'Assign this lead to another agent.';

  return (
    // One bordered chip the height of the buttons it shares the row with, and
    // `ml-auto` so it sits at the row's right end - under Call / WhatsApp, with
    // the same right edge. Loose text here read as a stray line between rows.
    <div className="border-border bg-muted/40 relative ml-auto flex h-9 items-center gap-2 rounded-md border pr-1 pl-2.5">
      <UserRound className="text-muted-foreground h-4 w-4 shrink-0" />
      <span className="text-muted-foreground text-xs whitespace-nowrap">Assigned agent</span>
      <span className="bg-border h-4 w-px shrink-0" aria-hidden />
      <span
        className={cn(
          'text-foreground max-w-52 truncate text-sm font-medium',
          // No pencil follows, so the name needs its own right padding.
          !editable && 'pr-1.5',
        )}
        title={
          editable
            ? ownerName
            : !canReassign
              ? "Only admins and sales leads can change a lead's owner."
              : 'This lead is closed - reopen it before reassigning.'
        }
      >
        {ownerName}
      </span>
      {editable && (
        <button
          type="button"
          onClick={toggle}
          disabled={approvalLocked || update.isPending}
          aria-expanded={open}
          aria-label="Change assigned agent"
          title={pencilTitle}
          className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Same click-away popover the list filters use for a searchable pick.
          Not a Radix select/dropdown: those route keystrokes into their own
          typeahead, so a search input nested inside never sees them. */}
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            onKeyDown={(event) => {
              if (event.key === 'Escape') setOpen(false);
            }}
            // Right-aligned: the control sits at the right edge of the header,
            // so a left-aligned panel would run off the screen.
            className="border-border bg-popover absolute top-full right-0 z-50 mt-1.5 flex max-h-80 w-64 flex-col rounded-md border p-1 shadow-md"
          >
            <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium tracking-wide uppercase">
              Assign to
            </div>
            <div className="border-border mb-1 flex items-center gap-1.5 border-b px-2 pb-1.5">
              <Search className="text-muted-foreground h-3.5 w-3.5 shrink-0" aria-hidden />
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus -- the popover exists to be typed into
                autoFocus
                type="text"
                value={query}
                aria-label="Search agents by name"
                placeholder="Search name…"
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && visible[0]) {
                    event.preventDefault();
                    pick(visible[0].value);
                  }
                }}
                className="text-foreground placeholder:text-muted-foreground h-7 w-full bg-transparent text-sm outline-none"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {agents.isLoading ? (
                <p className="text-muted-foreground px-2 py-3 text-center text-[13px]">Loading…</p>
              ) : visible.length === 0 ? (
                <p className="text-muted-foreground px-2 py-3 text-center text-[13px]">
                  No matches
                </p>
              ) : (
                visible.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={option.value === currentId}
                    onClick={() => pick(option.value)}
                    className="text-foreground hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm"
                  >
                    <Check
                      className="h-3.5 w-3.5 shrink-0"
                      aria-hidden
                      style={{ visibility: option.value === currentId ? 'visible' : 'hidden' }}
                    />
                    <span className="truncate">{option.label}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}

      <UzConfirmDialog
        open={pendingAgentId !== null}
        onOpenChange={(next) => {
          if (!next) setPendingAgentId(null);
        }}
        tone="primary"
        title="Reassign this lead?"
        description={`${lead.customer.displayName} (${lead.reference}) will move from ${ownerName} to ${pendingAgentName}, who will be notified.`}
        confirmLabel="Reassign"
        onConfirm={confirmReassign}
      />
    </div>
  );
};
